import { useEffect, useState } from "react";
import { useLoaderData, useLocation, useNavigate } from "@remix-run/react";
import { json, LoaderFunctionArgs } from "@remix-run/node";
import { Filter, MapPin, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import ButtonTheme from "../components/theme/Button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import Header from "../components/header";
import Footer from "../components/Footer";
import StoreCard from "../components/StoreCard";
import { getUser } from "../lib/database_interfaces/user";
import { getSupabaseWithSessionHeaders } from "../lib/supabase_server";
import { PUBLIC_STORE_PROFILES } from "~/lib/public_store_profiles";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "../components/ui/sheet";
import {
    calculateHaversineDistance,
    getCoordinatesFromZipCode,
} from "~/lib/helpers/storehelpers";
import { isValidUsZipCode, sanitizeUsZipInput, usZip5Base } from "~/lib/usZip";

export const loader = async ({ request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const zipParam = url.searchParams.get("zip")?.trim() ?? "";

    const { session, supabase } = await getSupabaseWithSessionHeaders({
        request,
    });

    let user;
    if (session?.user) {
        user = await getUser({ userId: session.user.id, dbClient: supabase });
    }

    const { data: stores, error: storesError } = await supabase
        .from(PUBLIC_STORE_PROFILES)
        .select("id, name, slug, banner_pic, zip, summary");

    if (storesError) {
        throw new Response(storesError.message, { status: 500 });
    }

    return json({
        stores: stores || [],
        userDetails: user || undefined,
        initialZip: zipParam,
    });
};

type StoreWithDistance = {
    id: string;
    name: string;
    slug: string | null;
    banner_pic: string | null;
    zip: string | null;
    summary: string | null;
    distance?: number;
};

export default function SuppliersPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { stores, userDetails, initialZip } = useLoaderData<typeof loader>();

    // Parse zip from URL
    const urlParams = new URLSearchParams(location.search);
    const zipFromUrl = urlParams.get("zip") || "";

    const [zipCode, setZipCode] = useState(() =>
        sanitizeUsZipInput(zipFromUrl || initialZip),
    );
    const [zipError, setZipError] = useState<string | null>(null);
    const [maxDistance, setMaxDistance] = useState<string>("50");
    const [sortBy, setSortBy] = useState(zipFromUrl ? "distance" : "a-z");
    const [searchFilter, setSearchFilter] = useState("");
    const [filteredStores, setFilteredStores] = useState<StoreWithDistance[]>(
        stores as StoreWithDistance[],
    );
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(24);
    const [isCalculating, setIsCalculating] = useState(false);

    // Sync zip code from URL changes (sanitize so only valid US ZIP characters remain)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const z = sanitizeUsZipInput(params.get("zip") || "");
        if (z !== zipCode) {
            setZipCode(z);
            setZipError(null);
        }
    }, [location.search]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [maxDistance, sortBy, searchFilter, zipCode]);

    // Apply filters & distance calculation
    useEffect(() => {
        const applyFilters = async () => {
            setIsCalculating(true);

            // Cache for zip -> coords lookups
            const coordsCache = new Map<
                string,
                { lat: number; lng: number } | null
            >();

            const getCoordsCached = async (zip: string | null) => {
                if (!zip) return null;
                const cacheKey = usZip5Base(zip);
                if (!cacheKey) return null;
                if (coordsCache.has(cacheKey)) return coordsCache.get(cacheKey)!;
                const coords = await getCoordinatesFromZipCode(zip);
                coordsCache.set(cacheKey, coords);
                return coords;
            };

            let userCoords: { lat: number; lng: number } | null = null;
            const activeZip = zipCode.trim();
            const zipForDistance = isValidUsZipCode(activeZip)
                ? activeZip
                : "";

            if (zipForDistance) {
                userCoords = await getCoordsCached(zipForDistance);
            }

            // Calculate distances for all stores
            const storesWithDistance: StoreWithDistance[] = await Promise.all(
                (stores as StoreWithDistance[]).map(async (store) => {
                    let distance: number | undefined = undefined;

                    if (userCoords && store.zip != null && store.zip !== "") {
                        const storeCoords = await getCoordsCached(
                            String(store.zip),
                        );
                        if (storeCoords) {
                            distance = calculateHaversineDistance(
                                userCoords.lat,
                                userCoords.lng,
                                storeCoords.lat,
                                storeCoords.lng,
                            );
                        }
                    }

                    return { ...store, distance };
                }),
            );

            // Apply filters
            let filtered = storesWithDistance.filter((store) => {
                // Name filter
                const matchesName = searchFilter === "" ||
                    store.name.toLowerCase().includes(
                        searchFilter.toLowerCase(),
                    );

                // Distance filter (only when zip is active)
                let matchesDistance = true;
                if (zipForDistance && maxDistance !== "0") {
                    if (store.distance === undefined) {
                        matchesDistance = false;
                    } else {
                        matchesDistance =
                            store.distance <= parseInt(maxDistance);
                    }
                }

                return matchesName && matchesDistance;
            });

            // Apply sorting
            switch (sortBy) {
                case "a-z":
                    filtered.sort((a, b) => a.name.localeCompare(b.name));
                    break;
                case "z-a":
                    filtered.sort((a, b) => b.name.localeCompare(a.name));
                    break;
                case "distance":
                    filtered.sort((a, b) => {
                        const dA = a.distance ?? Infinity;
                        const dB = b.distance ?? Infinity;
                        return dA - dB;
                    });
                    break;
                default:
                    break;
            }

            setFilteredStores(filtered);
            setIsCalculating(false);
        };

        applyFilters();
    }, [stores, maxDistance, sortBy, searchFilter, zipCode]);

    // Pagination
    const totalItems = filteredStores.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedStores = filteredStores.slice(startIndex, endIndex);

    const onZipCodeChange = (value: string) => {
        setZipError(null);
        setZipCode(sanitizeUsZipInput(value));
    };

    const handleZipSearch = () => {
        const trimmed = zipCode.trim();
        if (!trimmed) {
            setZipError(null);
            navigate("?");
            return;
        }
        if (!isValidUsZipCode(trimmed)) {
            setZipError(
                "Enter a valid US ZIP code (5 digits, or ZIP+4 as 12345-6789).",
            );
            return;
        }
        setZipError(null);
        setSortBy("distance");
        navigate(`?zip=${encodeURIComponent(trimmed)}`);
    };

    const clearAllFilters = () => {
        setZipCode("");
        setZipError(null);
        setMaxDistance("50");
        setSortBy("a-z");
        setSearchFilter("");
        navigate("?");
    };

    // Pagination component (same pattern as search page)
    const Pagination = () => {
        if (totalPages <= 1) return null;

        const handlePageChange = (newPage: number) => {
            setCurrentPage(newPage);
        };

        const getPageNumbers = () => {
            const pages: (number | string)[] = [];
            const maxVisiblePages = 5;

            if (totalPages <= maxVisiblePages) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
            } else {
                if (currentPage <= 3) {
                    for (let i = 1; i <= 4; i++) pages.push(i);
                    pages.push("...");
                    pages.push(totalPages);
                } else if (currentPage >= totalPages - 2) {
                    pages.push(1);
                    pages.push("...");
                    for (let i = totalPages - 3; i <= totalPages; i++) {
                        pages.push(i);
                    }
                } else {
                    pages.push(1);
                    pages.push("...");
                    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                        pages.push(i);
                    }
                    pages.push("...");
                    pages.push(totalPages);
                }
            }

            return pages;
        };

        return (
            <div className="flex flex-col sm:flex-row items-center justify-between mt-6 gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                    Showing {startIndex + 1} to {Math.min(endIndex, totalItems)}
                    {" "}
                    of {totalItems} suppliers
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                            handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="text-xs sm:text-sm"
                    >
                        <span className="hidden sm:inline">Previous</span>
                        <span className="sm:hidden">Prev</span>
                    </Button>

                    <div className="flex items-center gap-1">
                        {getPageNumbers().map((page, index) => (
                            <div key={index}>
                                {page === "..."
                                    ? (
                                        <span className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-muted-foreground">
                                            ...
                                        </span>
                                    )
                                    : (
                                        <Button
                                            variant={currentPage === page
                                                ? "default"
                                                : "outline"}
                                            size="sm"
                                            onClick={() =>
                                                handlePageChange(
                                                    page as number,
                                                )}
                                            className="w-7 h-7 sm:w-8 sm:h-8 p-0 text-xs sm:text-sm"
                                        >
                                            {page}
                                        </Button>
                                    )}
                            </div>
                        ))}
                    </div>

                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                            handlePageChange(
                                Math.min(totalPages, currentPage + 1),
                            )}
                        disabled={currentPage === totalPages}
                        className="text-xs sm:text-sm"
                    >
                        Next
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <>
            <Header
                userId={userDetails?.id || ""}
                userName={userDetails?.first_name || ""}
                isAdmin={Boolean(userDetails?.admin)}
            />
            <div className="min-h-screen bg-background">
                <div className="container mx-auto mt-16 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-full">
                    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                        {/* Desktop Filters Sidebar */}
                        <div className="hidden lg:block w-64 flex-shrink-0">
                            <div className="sticky top-24">
                                <FilterSidebar sortBy={sortBy} sortByCallback={setSortBy} zipCode={zipCode} zipCodeCallback={onZipCodeChange} zipError={zipError} handleZipSearch={handleZipSearch} maxDistance={maxDistance} maxDistanceCallback={setMaxDistance} clearAllFilters={clearAllFilters} searchFilter={searchFilter} searchFilterCallback={setSearchFilter} />
                                {/* <FilterSidebar /> */}
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="flex-1 min-w-0 w-full">
                            {/* Results Header */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
                                <div className="flex-1 min-w-0">
                                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">
                                        All Suppliers
                                    </h1>
                                    <p className="text-sm sm:text-base text-muted-foreground mt-1">
                                        {isCalculating
                                            ? (
                                                "Calculating distances..."
                                            )
                                            : (
                                                <>
                                                    Showing{" "}
                                                    {filteredStores.length}{" "}
                                                    supplier
                                                    {filteredStores.length !== 1
                                                        ? "s"
                                                        : ""}
                                                    {isValidUsZipCode(zipCode) && (
                                                        <span className="ml-1">
                                                            near {zipCode}
                                                            {sortBy ===
                                                                    "distance" &&
                                                                " • Sorted by distance"}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                                    {/* Mobile Filter Button */}
                                    <Sheet>
                                        <SheetTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="lg:hidden flex-1 sm:flex-none"
                                            >
                                                <Filter className="h-4 w-4 mr-2" />
                                                <span className="hidden sm:inline">
                                                    Filters
                                                </span>
                                            </Button>
                                        </SheetTrigger>
                                        <SheetContent
                                            side="left"
                                            className="w-[280px] sm:w-[320px] overflow-y-auto"
                                        >
                                            <SheetHeader className="pb-4">
                                                <SheetTitle>Filters</SheetTitle>
                                            </SheetHeader>
                                            <div className="px-1">
                                            <FilterSidebar sortBy={sortBy} sortByCallback={setSortBy} zipCode={zipCode} zipCodeCallback={onZipCodeChange} zipError={zipError} handleZipSearch={handleZipSearch} maxDistance={maxDistance} maxDistanceCallback={setMaxDistance} clearAllFilters={clearAllFilters} searchFilter={searchFilter} searchFilterCallback={setSearchFilter} />
                                            </div>
                                        </SheetContent>
                                    </Sheet>

                                    {/* Search Filter */}
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            type="text"
                                            placeholder="Filter results..."
                                            value={searchFilter}
                                            onChange={(e) =>
                                                setSearchFilter(e.target.value)}
                                            className="pl-10 w-48 sm:w-56"
                                        />
                                    </div>

                                    {/* Sort (visible on mobile as quick access) */}
                                    <Select
                                        value={sortBy}
                                        onValueChange={setSortBy}
                                    >
                                        <SelectTrigger className="w-full sm:w-40 lg:hidden">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="a-z">
                                                A → Z
                                            </SelectItem>
                                            <SelectItem value="z-a">
                                                Z → A
                                            </SelectItem>
                                            {isValidUsZipCode(zipCode) && (
                                                <SelectItem value="distance">
                                                    Distance
                                                </SelectItem>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Active Filters */}
                            {(isValidUsZipCode(zipCode) ||
                                searchFilter.length > 0 ||
                                maxDistance !== "50") && (
                                <div className="mb-4 sm:mb-6">
                                    <div className="flex flex-wrap gap-1 sm:gap-2">
                                        {isValidUsZipCode(zipCode) && (
                                            <Badge
                                                variant="secondary"
                                                className="cursor-pointer text-xs sm:text-sm"
                                                onClick={() => {
                                                    setZipCode("");
                                                    setZipError(null);
                                                    navigate("?");
                                                }}
                                            >
                                                ZIP: {zipCode} ×
                                            </Badge>
                                        )}
                                        {isValidUsZipCode(zipCode) &&
                                            maxDistance !== "0" && (
                                            <Badge
                                                variant="secondary"
                                                className="cursor-pointer text-xs sm:text-sm"
                                                onClick={() =>
                                                    setMaxDistance("0")}
                                            >
                                                Within {maxDistance} miles ×
                                            </Badge>
                                        )}
                                        {searchFilter.length > 0 && (
                                            <Badge
                                                variant="secondary"
                                                className="cursor-pointer text-xs sm:text-sm"
                                                onClick={() =>
                                                    setSearchFilter("")}
                                            >
                                                Filter: {searchFilter} ×
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Results Grid */}
                            {filteredStores.length > 0
                                ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5">
                                        {paginatedStores.map((store) => (
                                            <StoreCard
                                                key={store.id}
                                                name={store.name}
                                                imageSrc={store.banner_pic}
                                                fluid
                                                detail={store.distance !==
                                                        undefined
                                                    ? `${
                                                        store.distance.toFixed(
                                                            1,
                                                        )
                                                    } mi`
                                                    : undefined}
                                            />
                                        ))}
                                    </div>
                                )
                                : (
                                    <div className="text-center py-6 sm:py-8 lg:py-12">
                                        <div className="text-muted-foreground mb-4">
                                            <Search className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-4 opacity-50" />
                                            <h3 className="text-base sm:text-lg font-semibold mb-2">
                                                No suppliers found
                                            </h3>
                                            <p className="text-sm sm:text-base">
                                                Try adjusting your search or
                                                filters
                                            </p>
                                        </div>
                                        <Button
                                            variant="outline"
                                            onClick={clearAllFilters}
                                            className="w-full sm:w-auto"
                                        >
                                            Clear All Filters
                                        </Button>
                                    </div>
                                )}

                            {/* Pagination */}
                            <Pagination />
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
}


interface FilterSidebarProps {
    zipCode: string;
    zipCodeCallback: (value: string) => void;
    zipError?: string | null;
    handleZipSearch: () => void;
    maxDistance: string;
    maxDistanceCallback: (value: string) => void;
    sortBy: string;
    sortByCallback: (value: string) => void;
    clearAllFilters: () => void;
    searchFilter: string;
    searchFilterCallback: (value: string) => void;
}
function FilterSidebar( { zipCode, zipCodeCallback, zipError, maxDistance, maxDistanceCallback, sortBy, sortByCallback, clearAllFilters, searchFilter, searchFilterCallback, handleZipSearch }: FilterSidebarProps ) {
   return (
    <div className="space-y-4 sm:space-y-6">
    {/* Zip Code */}
    <div>
        <h3 className="font-semibold mb-2 sm:mb-3 text-sm sm:text-base flex items-center">
            <MapPin className="h-4 w-4 mr-2" />
            Zip Code
        </h3>
        <div className="flex gap-2">
            <Input
                type="text"
                inputMode="numeric"
                autoComplete="postal-code"
                placeholder="Enter ZIP code"
                value={zipCode}
                onChange={(e) => zipCodeCallback(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        handleZipSearch();
                    }
                }}
                className="flex-1 text-sm"
            />
            <ButtonTheme
                type="button"
                mode="primary"
                size="icon"
                onClick={handleZipSearch}
                className="shrink-0"
                aria-label="Search by ZIP code"
            >
                <Search className="h-4 w-4" />
            </ButtonTheme>
        </div>
        {zipError && (
            <p className="text-xs text-destructive mt-1" role="alert">
                {zipError}
            </p>
        )}
        {isValidUsZipCode(zipCode) && (
            <p className="text-xs text-muted-foreground mt-1">
                Searching near: {zipCode}
            </p>
        )}
    </div>

    <Separator />

    {/* Maximum Distance */}
    <div>
        <h3 className="font-semibold mb-2 sm:mb-3 text-sm sm:text-base flex items-center">
            <MapPin className="h-4 w-4 mr-2" />
            Maximum Distance
        </h3>
        <Select value={maxDistance} onValueChange={maxDistanceCallback}>
            <SelectTrigger className="w-full text-sm">
                <SelectValue placeholder="Select maximum distance..." />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="0">No limit</SelectItem>
                <SelectItem value="10">Within 10 miles</SelectItem>
                <SelectItem value="25">Within 25 miles</SelectItem>
                <SelectItem value="50">Within 50 miles</SelectItem>
                <SelectItem value="100">Within 100 miles</SelectItem>
                <SelectItem value="250">Within 250 miles</SelectItem>
            </SelectContent>
        </Select>
        {!isValidUsZipCode(zipCode) && maxDistance !== "0" && (
            <p className="text-xs text-muted-foreground mt-1">
                Enter a ZIP code to filter by distance
            </p>
        )}
    </div>

    <Separator />

    {/* Sort By */}
    <div>
        <h3 className="font-semibold mb-2 sm:mb-3 text-sm sm:text-base">
            Sort By
        </h3>
        <Select value={sortBy} onValueChange={sortByCallback}>
            <SelectTrigger className="w-full text-sm">
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="a-z">A → Z</SelectItem>
                <SelectItem value="z-a">Z → A</SelectItem>
                {isValidUsZipCode(zipCode) && (
                    <SelectItem value="distance">Distance</SelectItem>
                )}
            </SelectContent>
        </Select>
    </div>

    <Separator />

    <Button
        variant="outline"
        onClick={clearAllFilters}
        className="w-full text-sm"
    >
        Clear All Filters
    </Button>
</div>
);
}